import { blue, blueGrey, deepPurple, green, teal } from "@mui/material/colors";
import {
  PiMicrosoftWordLogoFill,
  PiMicrosoftExcelLogoFill,
  PiMicrosoftPowerpointLogoFill,
} from "react-icons/pi";
import { RiFilePdf2Line } from "react-icons/ri";
import { LuFileText } from "react-icons/lu";
import { FaRegFile } from "react-icons/fa";
import { FiTable } from "react-icons/fi";

const PLATFORM_TYPE = window.ENV?.PLATFORM_TYPE;

export function getFormattedDate() {
  const d = new Date();

  const formattedDate =
    d.getFullYear() +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + d.getDate()).slice(-2) +
    "T" +
    ("0" + d.getHours()).slice(-2) +
    ("0" + d.getMinutes()).slice(-2) +
    ("0" + d.getSeconds()).slice(-2);

  return formattedDate;
}

// default filter object
export const filterList = (filterString: string) => {
  try {
    const parsedFilters = JSON.parse(filterString);
    return parsedFilters ? parsedFilters : [];
  } catch (e) {
    console.error("Could not parse filterString as JSON:", filterString, e);
    return [];
  }
};

export const generateUniqueKey = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const isObjEmpty = (obj: Record<string, unknown>) => {
  return Object.keys(obj).length === 0;
};

export const isFastDraft = () => {
  switch (PLATFORM_TYPE) {
    case "fastdraft":
      return true;
    default:
      return false;
  }
};

/**
 * FastDraft-branded deployment, tolerant of either flag. Release configs and
 * `upgrade.sh` in platform-releases only set `PLATFORM_TYPE` (what
 * `isFastDraft` reads); `IS_FASTDRAFT` survives in older `config.js` files
 * and in `index.html`, so accept either rather than silently un-branding a
 * deployment that sets only one.
 */
export const isFastDraftDeployment = () =>
  isFastDraft() || window.ENV?.IS_FASTDRAFT === true;

export const determinePlatformType = () => {
  switch (PLATFORM_TYPE) {
    case "fastdraft":
      return "FastDraft";
    case "nuclearn":
      return "AtomAssist";
    default:
      return "AtomAssist";
  }
};

export const determineLoginPlatformType = () => {
  switch (PLATFORM_TYPE) {
    case "fastdraft":
      return "FastDraft";
    case "nuclearn":
      return "Nuclearn";
    default:
      return "Nuclearn";
  }
};

export const determinePlatformLinkText = () => {
  switch (PLATFORM_TYPE) {
    case "fastdraft":
      return "fast-draft.ai";
    case "nuclearn":
      return "Nuclearn.ai";
    default:
      return "Nuclearn.ai";
  }
};

export const determinePlatformLink = () => {
  switch (PLATFORM_TYPE) {
    case "fastdraft":
      return "https://www.fast-draft.ai";
    case "nuclearn":
      return "https://www.nuclearn.ai";
    default:
      return "https://www.nuclearn.ai";
  }
};

export const snakeToTitleCase = (str: string) => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/** Safely parse JSON with error handling */
export const safeJsonParse = (data: unknown) => {
  try {
    const parsed = typeof data === "string" ? JSON.parse(data) : data;
    return { success: true, data: parsed };
  } catch (e) {
    return { success: false, data: null, error: String(e) };
  }
};

export const getColorFromPersona = (name: string) => {
  const colors = [
    teal[300],
    blue[300],
    green[300],
    blueGrey[300],
    deepPurple[300],
  ];

  const hash = name.split("").reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);

  const index = hash % colors.length;
  return colors[index];
};

export const truncateText = (
  text: string | undefined | null,
  length: number,
) => {
  if (!text) return "";
  return text.length > length ? text.slice(0, length) + "..." : text;
};

export const truncateMiddle = (text: string, maxLength: number) => {
  if (text?.length <= maxLength) return text;

  const charsToShow = maxLength - 3; // Account for the 3 dots
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return (
    text?.substring(0, frontChars) +
    "..." +
    text?.substring(text.length - backChars)
  );
};

/**
 * Parse an API timestamp as the UTC instant it names.
 *
 * The API stores and sends UTC but omits the zone (`2026-10-12T14:00:00`),
 * and `new Date` reads a zone-less string as browser-local time. A string
 * that already carries a zone (`Z` or a ±hh:mm offset) is parsed as-is, so
 * this stays correct if the API starts labelling its times. The
 * space-separated `YYYY-MM-DD HH:MM:SS` form is accepted too.
 */
export function parseApiDate(value: string): Date {
  const iso = value.replace(/^(\d{4}-\d{2}-\d{2}) /, "$1T");
  // Appending 'Z' to a zoned string would make it Invalid Date.
  return new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
}

export function timeAgo(inputDate: string) {
  const parsedDate = parseApiDate(inputDate);

  // Get the current date in local time zone
  const now = new Date();

  // Calculate the time difference in seconds
  const seconds = Math.floor((now.getTime() - parsedDate.getTime()) / 1000);

  // Under a minute ahead is clock skew (server-minted timestamps vs. a
  // workstation clock a few seconds behind), not a future date.
  if (seconds < -60) {
    return "in the future";
  }

  // Define time intervals in seconds
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  // Determine the appropriate time unit
  for (const [unit, value] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / value);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
}

export function debounce(func: (...args: any[]) => void, wait: number) {
  let timeoutId: NodeJS.Timeout | null = null;

  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeoutId!);
      func(...args);
    };

    clearTimeout(timeoutId!);
    timeoutId = setTimeout(later, wait);
  };
}

export function areWhereFiltersValid(filters: any[]) {
  if (!filters || filters.length <= 0) {
    return false;
  }

  let isValid = true;

  filters.forEach((filter) => {
    // Check if filter exists
    if (!filter) {
      isValid = false;
    }

    // Check if name is valid
    if (
      filter.filter_value === null ||
      filter.filter_value === undefined ||
      filter.filter_value === ""
    ) {
      isValid = false;
    }

    // Check if filter_on exists and has a valid value
    if (
      !filter.filter_on ||
      filter.filter_on.name === null ||
      filter.filter_on.name === undefined ||
      filter.filter_on.name === ""
    ) {
      isValid = false;
    }
  });

  return isValid;
}

export const getFileIconAndColor = ({
  fileName,
  fontSize = 24,
}: {
  fileName: string;
  fontSize?: number;
}) => {
  const extension = fileName?.split(".").pop()?.toLowerCase();

  const iconStyle = { fontSize };

  switch (extension) {
    case "pdf":
      return {
        icon: <RiFilePdf2Line style={{ ...iconStyle, color: "#EA4335" }} />,
        color: "#EA4335",
      };

    case "doc":
    case "docx":
      return {
        icon: (
          <PiMicrosoftWordLogoFill style={{ ...iconStyle, color: "#2B579A" }} />
        ),
        color: "#2B579A",
      };

    case "txt":
      return {
        icon: <LuFileText style={{ ...iconStyle, color: "#4285F4" }} />,
        color: "#4285F4",
      };

    case "xls":
    case "xlsx":
      return {
        icon: (
          <PiMicrosoftExcelLogoFill
            style={{ ...iconStyle, color: "#1D6F42" }}
          />
        ),
        color: "#1D6F42",
      };

    case "csv":
      return {
        icon: <FiTable style={{ ...iconStyle, color: "#34A853" }} />,
        color: "#34A853",
      };
    case "ppt":
    case "pptx":
      return {
        icon: (
          <PiMicrosoftPowerpointLogoFill
            style={{ ...iconStyle, color: "#D04423" }}
          />
        ),
        color: "#D04423",
      };

    default:
      return {
        icon: <FaRegFile style={{ ...iconStyle, color: "black" }} />,
        color: "black",
      };
  }
};
